import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Share2, Eye, EyeOff, MoreHorizontal, ChevronLeft } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Post, Gym } from "./types";
import { useNotifications } from "@/hooks/useNotifications";

interface PostDialogProps {
  post: Post | null;
  gym: Gym | null;
  isHidden: boolean;
  onClose: () => void;
  onShare: (post: Post) => void;
  onToggleVisibility: (postId: string) => void;
}

export const PostDialog = ({ 
  post, 
  gym, 
  isHidden,
  onClose, 
  onShare,
  onToggleVisibility 
}: PostDialogProps) => {
  const formatDate = (date: string) => {
    return formatDistanceToNow(new Date(date), { 
      addSuffix: true,
      locale: es 
    });
  };

  if (!post) return null;

  return (
    <Dialog open={!!post} onOpenChange={onClose}>
      <DialogContent className="max-w-lg sm:max-w-2xl w-[95vw] h-[85vh] max-h-[700px] p-0 border-0 rounded-3xl overflow-hidden bg-transparent">
        <article className="relative w-full h-full">
          {/* Full background image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: post.image_url 
                ? `url(${post.image_url})` 
                : 'linear-gradient(to bottom right, rgba(0,0,0,0.3), rgba(0,0,0,0.1))'
            }}
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

          {/* Top controls */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4">
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors">
                  <MoreHorizontal className="h-5 w-5 text-white" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onShare(post)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Compartir
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleVisibility(post.id)}>
                  {isHidden ? (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      Mostrar noticia
                    </>
                  ) : (
                    <>
                      <EyeOff className="mr-2 h-4 w-4" />
                      Ocultar noticia
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Bottom content over gradient */}
          <div className="absolute bottom-0 left-0 right-0 p-6 space-y-3 max-h-[60%] overflow-y-auto">
            <Badge 
              className="bg-clubbers-red hover:bg-clubbers-red/90"
              variant="secondary"
            >
              Deportes
            </Badge>

            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white">
              {post.title}
            </h2>

            <div className="flex items-center gap-2 text-sm text-white/70">
              {gym && (
                <>
                  <div className="w-5 h-5 rounded-full bg-clubbers-red flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {gym.name.charAt(0)}
                  </div>
                  <span className="truncate">{gym.name}</span>
                  <span>·</span>
                </>
              )}
              <span>{formatDate(post.created_at)}</span>
            </div>

            <p className="text-sm sm:text-base text-white/80 leading-relaxed">
              {post.content}
            </p>
          </div>
        </article>
      </DialogContent>
    </Dialog>
  );
};
